import React, { useEffect } from 'react';
import { useAuthPin, useI18n, useSpringBigStore } from '@springbig/shared';
import { FormControl, HStack, VStack, WarningIcon } from 'native-base';
import { Caption, H3, H2 } from '~/shared/Typography';
import { AppRouteProps, useAppNavigate } from '~/pages';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button, Input } from '~/shared/Theme';
import { NavigateOptions, Params } from 'react-router';

type BirthdayStepProps = {
    onEvent: (message: string, value?: boolean) => void;
    navigate: (route: AppRouteProps, opts?: NavigateOptions) => void;
    params: Params<string>;
    setPasskeyEnabled: (value: boolean) => void;
};

export const BirthdayStep = ({
    onEvent,
    navigate,
    params,
    setPasskeyEnabled
}: BirthdayStepProps) => {

    const { t } = useI18n();
    const { birthdays, birthdayInput } = useSpringBigStore();

    const { mutate: authPin, error, isError, isLoading } = useAuthPin();

    if (!navigate) {
        navigate = useAppNavigate().navigate;
    }

    if (!params) {
        params = useAppNavigate().params;
    }

    const submitBirthday = (birthday: string): void => {

        authPin({
            birthday,
            reference: 'member',
            stashboardOrMemberKey: params.wallet_id,
        }, {
            onSuccess: (response) => {
                setPasskeyEnabled(response.passkey_enabled);

                if (response.step) {
                    onEvent(response.step, response.passkey_enabled);
                }
            }
        });
    };

    useEffect(() => {

        if (!birthdays) {
            navigate(`w/${params.wallet_id}/`);
        }
    }, [birthdays]);

    const schema = yup.object().shape({
        birthday: yup
            .string()
            .required('birthday is required')
            .trim()
            .matches(
                /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/,
                'Birthday must be in the format MM/DD/YYYY'
            )
    });

    const {
        control,
        handleSubmit,
        clearErrors,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema)
    });

    const { field: birthdayField } = useController({
        control,
        defaultValue: '',
        name: 'birthday',
    });

    const onSubmit = (data: FormData): void => {

        const birthday = data.birthday;
        authPin({
            birthday,
            stashboardOrMemberKey: params.wallet_id,
        }, {
            onSuccess: (response) => {
                setPasskeyEnabled(response.passkey_enabled);

                if (response.step) {
                    onEvent(response.step, response.passkey_enabled);
                }
            }
        });
    };

    const onBirthdayChange = (value: string): void => {

        clearErrors('birthday');
        const numericValue = value.replace(/\D/g, '');

        let formattedValue = numericValue.replace(/^(.{2})/, '$1/');
        formattedValue = formattedValue.replace(/^(.{5})/, '$1/');
        const limitedValue = formattedValue.slice(0, 10);

        birthdayField.onChange(limitedValue);
    };

    return (
        birthdayInput === 'freehand' ?
            <VStack space={8} p={8} w={'100%'} alignItems={'center'}>
                <VStack textAlign={'center'}>
                    <H2>please confirm your birthday</H2>
                    <Caption>
                        enter your birthday in order to continue
                    </Caption>
                </VStack>
                <FormControl isInvalid={!!errors.birthday}>
                    <FormControl.Label>
                        <Caption text={'auth.ten_dlc.birthday.error'} />
                    </FormControl.Label>
                    <Input
                        {...birthdayField}
                        isInvalid={!!errors.birthday}
                        isDisabled={isLoading}
                        onChangeText={onBirthdayChange}
                        maxLength={255}
                        returnKeyType={'done'}
                        placeholder={'MM/DD/YYYY'}
                    />
                    <FormControl.ErrorMessage
                        leftIcon={<WarningIcon size={3} />}
                    >
                        <Caption
                            fontSize={12}
                            color={'red'}
                            textAlign={'center'}
                        >
                            { errors.birthday?.message }
                        </Caption>
                    </FormControl.ErrorMessage>
                    { isError &&
                        <Caption
                            fontSize={12}
                            color={'red'}
                            textAlign={'center'}
                            fontFamily="Helvetica"
                        >
                            { (error?.data?.error) as any }
                        </Caption>
                    }
                </FormControl>
                <Button
                    text={'auth.ten_dlc.birthday.confirm'}
                    onPress={handleSubmit(onSubmit)}
                    isLoading={isLoading}
                />
            </VStack>
            :
            birthdays && birthdays.length > 0 ?

                <VStack space={8} p={8} w={'100%'} alignItems={'center'}>
                    <VStack>
                        <H3
                            fontSize={20}
                            text={'auth.ten_dlc.birthday.title'}
                            fontFamily="Helvetica"
                        />
                        <Caption text={'auth.ten_dlc.birthday.description'} fontFamily="Helvetica" />
                    </VStack>
                    { isError &&
                        <Caption
                            fontSize={12}
                            color={'red'}
                            textAlign={'center'}
                            fontFamily="Helvetica"
                        >
                            { (error?.data?.error) as any }
                        </Caption>
                    }
                    <VStack space={4} w={'100%'}>
                        <HStack
                            space={4}
                            flexWrap={'wrap'}
                        >
                            { birthdays.slice(0, 2).map((birthday, index) =>

                                <Button
                                    key={index}
                                    text={birthday}
                                    onPress={() => submitBirthday(birthday)}
                                    flex={1}
                                />
                            )}
                        </HStack>
                        <HStack
                            space={4}
                            flexWrap={'wrap'}
                        >
                            { birthdays.slice(2, 4).map((birthday, index) =>

                                <Button
                                    key={index}
                                    text={birthday}
                                    onPress={() => submitBirthday(birthday)}
                                    flex={1}
                                />
                            )}
                        </HStack>
                    </VStack>
                </VStack>
                :
                <Caption>
                    An error occurred, could not fetch birthday from database
                </Caption>
    );
};
